import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Users, Eye, Bot, MessageSquare, RefreshCw, Lock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <div data-testid={testId} className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
    <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider font-bold">
      <Icon size={14} className="text-[#2ec4b6]" /> {label}
    </div>
    <div className="text-3xl font-bold text-white mt-2 font-heading">{value}</div>
    {sub && <div className="text-xs text-zinc-400 mt-1">{sub}</div>}
  </div>
);

const Panel = ({ title, children, testId }) => (
  <div data-testid={testId} className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
    <h2 className="text-sm font-bold text-white mb-4">{title}</h2>
    {children}
  </div>
);

const KeyGate = ({ onSubmit, error }) => {
  const [value, setValue] = useState("");
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(value.trim()); }}
        className="bg-white/[0.03] border border-white/10 rounded-3xl p-8 w-full max-w-sm"
        data-testid="admin-key-form"
      >
        <div className="flex items-center gap-2 text-[#2ec4b6] mb-4"><Lock size={18} /><span className="font-bold">Site Analytics</span></div>
        <p className="text-sm text-zinc-400 mb-4">Enter the admin key to view the dashboard.</p>
        <input
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Admin key"
          data-testid="admin-key-input"
          className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#2ec4b6]"
        />
        {error && <p data-testid="admin-key-error" className="text-red-400 text-xs mt-2">{error}</p>}
        <button type="submit" data-testid="admin-key-submit" className="w-full mt-4 bg-[#2ec4b6] text-black font-bold rounded-xl py-2.5 text-sm hover:bg-[#26a89c] transition-colors">
          View analytics
        </button>
      </form>
    </div>
  );
};

export default function AdminAnalytics() {
  const [key, setKey] = useState(() => localStorage.getItem("wcifmd_admin_key") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/api/admin/analytics`, { headers: { "X-Admin-Key": k } });
      setData(res.data);
      localStorage.setItem("wcifmd_admin_key", k);
    } catch (e) {
      setData(null);
      if (e.response?.status === 401) {
        localStorage.removeItem("wcifmd_admin_key");
        setError("Invalid admin key");
      } else {
        setError("Could not load analytics — try again");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Site Analytics | wherecaniflymydrone";
    if (key) load(key);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <KeyGate onSubmit={(k) => { setKey(k); load(k); }} error={error} />;

  const t = data.totals;
  const classLabels = { sub250: "Under 250g", a2: "250g–900g", heavy: "Over 900g" };

  return (
    <div data-testid="analytics-dashboard" className="min-h-screen bg-[#0a0a0a] text-zinc-100">
      <div className="max-w-6xl mx-auto px-6 pt-10 pb-20">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" data-testid="analytics-back-home" className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-[#2ec4b6] transition-colors">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <button onClick={() => load(key)} data-testid="analytics-refresh" className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-[#2ec4b6] transition-colors">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Site Analytics</h1>
        <p className="text-sm text-zinc-400 mt-2">Human visitors only — bot and crawler traffic is filtered out automatically. Last 30 days.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <StatCard icon={Users} label="Visitors (30d)" value={t.visitors_30d} sub={`${t.visitors_today} today`} testId="stat-visitors" />
          <StatCard icon={Eye} label="Page views (30d)" value={t.views_30d} sub={`${t.views_today} today`} testId="stat-views" />
          <StatCard icon={MessageSquare} label="AI clearance checks" value={t.searches_30d} sub="last 30 days" testId="stat-searches" />
          <StatCard icon={Bot} label="Bot hits filtered" value={t.bot_hits_30d} sub="excluded from stats" testId="stat-bots" />
        </div>

        <div className="mt-6">
          <Panel title="Daily visitors — last 30 days" testId="panel-daily-chart">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.daily} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                  <XAxis dataKey="day" tick={{ fill: "#71717a", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#71717a", fontSize: 10 }} allowDecimals={false} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #333", borderRadius: 12, fontSize: 12 }} labelStyle={{ color: "#a1a1aa" }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="visitors" fill="#2ec4b6" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="views" fill="#2ec4b640" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <Panel title="Top pages" testId="panel-top-pages">
            {data.top_pages.length === 0 && <p className="text-sm text-zinc-500">No page views recorded yet.</p>}
            <ul className="space-y-2">
              {data.top_pages.map((p) => (
                <li key={p.path} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-300 truncate mr-4">{p.path}</span>
                  <span className="text-[#2ec4b6] font-bold shrink-0">{p.views}</span>
                </li>
              ))}
            </ul>
          </Panel>
          <Panel title="Top referrers" testId="panel-referrers">
            {data.top_referrers.length === 0 && <p className="text-sm text-zinc-500">No external referrers yet — this fills up as search traffic arrives.</p>}
            <ul className="space-y-2">
              {data.top_referrers.map((r) => (
                <li key={r.referrer} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-300 truncate mr-4">{r.referrer}</span>
                  <span className="text-[#2ec4b6] font-bold shrink-0">{r.views}</span>
                </li>
              ))}
            </ul>
          </Panel>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <Panel title="Recent AI clearance checks" testId="panel-recent-searches">
            {data.searches.recent.length === 0 && <p className="text-sm text-zinc-500">No clearance checks yet.</p>}
            <ul className="space-y-2">
              {data.searches.recent.map((s, i) => (
                <li key={`${s.created_at}-${i}`} className="flex items-center justify-between text-sm gap-3">
                  <span className="text-zinc-300 truncate">{s.search_query}</span>
                  <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${s.is_safe_outcome ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                    {s.is_safe_outcome ? "SAFE" : "RESTRICTED"}
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
          <Panel title="Drone classes checked" testId="panel-class-dist">
            <ul className="space-y-3">
              {data.searches.class_distribution.map((c) => (
                <li key={c.weight_class} className="text-sm">
                  <div className="flex justify-between text-zinc-300 mb-1">
                    <span>{classLabels[c.weight_class] || c.weight_class}</span>
                    <span className="text-[#2ec4b6] font-bold">{c.count}</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-[#2ec4b6]" style={{ width: `${Math.min(100, (c.count / Math.max(1, data.searches.safe + data.searches.no_fly)) * 100)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex gap-6 mt-5 text-sm">
              <div><span className="text-emerald-400 font-bold">{data.searches.safe}</span> <span className="text-zinc-400">safe outcomes</span></div>
              <div><span className="text-red-400 font-bold">{data.searches.no_fly}</span> <span className="text-zinc-400">restricted</span></div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
